/**
 * Shared TypeBox schemas for `POST /chat` responses that need to be
 * referenced by both the hub (route definition) and downstream clients.
 *
 * Streaming success responses (SSE) are intentionally NOT modeled here —
 * the route hijacks the reply and pipes a UI message stream directly.
 * Only typed JSON error responses live in this file.
 *
 * Cites: TOOL-05, COP-015, D-15 (typed-error → 422 wire format),
 *        D-37d (OpenCode HIGH + LOW replan-fixes — warnings carried in the
 *        422 body for operator-facing compiler skip-reasons + explicit
 *        response schema so Fastify's `onRoute` hook does not silently
 *        bypass response validation on the new 422 branch).
 */
import { Type } from '@sinclair/typebox';
import { UuidSchema } from './common.js';
/**
 * Plan 05-09 (TASK-HUB-18 / H7 / APPR-05 / D-13) — shared `POST /chat` body
 * schema. The hub route imports this schema directly so the
 * `pendingActionId` field is declared in EXACTLY ONE place.
 *
 * Field set:
 *   - `messages: UIMessage[]` (optional since COP-160 D11) — opaque (typed
 *     by the AI SDK); a released client's history array, of which the route
 *     takes only the last `user` entry. We do NOT type-narrow the array
 *     element schema because the AI SDK's UIMessage union changes across
 *     minor versions and we want the route to accept the shape verbatim.
 *   - `message: UIMessage` (optional, COP-160 D11) — the new client's ONE
 *     new user message; opaque for the same reason.
 *   - `trackName: string` — the resolved track slug.
 *   - `sessionId: UuidSchema` (optional) — pre-existing chat session.
 *   - `context: object` (optional) — opaque ambient context blob.
 *   - (COP-092 retire plan-mode: the `planMode` body flag is removed.)
 *   - `pendingActionId: UuidSchema` (optional) — continuation-turn approval
 *     id. When set, the runtime executes the bound `tf_approval.payload`
 *     directly without re-prompting the model (H2).
 *   - `pendingPlanId: UuidSchema` (optional) — continuation-turn plan id.
 *     When set, the executing-plan runtime resumes the server-persisted
 *     `tf_plan.steps_jsonb` envelope instead of trusting client-supplied
 *     steps. The schema rejects requests that carry both continuation ids;
 *     callers must choose exactly one resume contract.
 *
 * `additionalProperties: false` REMAINS on the route schema — continuation
 * fields are EXPLICITLY declared, not smuggled (H7 — Fastify rejects unknown
 * properties as 400 before the handler runs).
 *
 * Cites: H7, APPR-05, D-13, COP-013, COP-046, COP-057.
 */
const ChatBodyBaseProperties = {
    // COP-160 D11 — a released client's full history array. The hub takes at
    // most its LAST entry, and only when `role = 'user'`; it never supplies
    // trusted history (the route loads the session history from the DB).
    messages: Type.Optional(Type.Array(Type.Unknown())),
    // COP-160 D11 — the ONE new user message of this turn (new clients). The
    // route decides whether a body without `message`/`messages` is acceptable
    // (a continuation) or `400 missing_user_message`.
    message: Type.Optional(Type.Unknown()),
    trackName: Type.String({ minLength: 1 }),
    sessionId: Type.Optional(UuidSchema),
    context: Type.Optional(Type.Object({}, { additionalProperties: true })),
    // COP-092 retire plan-mode: the `planMode` per-turn activation flag is removed.
};
/**
 * Flat schema (not a union) to avoid Fastify's AJV `removeAdditional: true`
 * silently stripping `pendingActionId`/`pendingPlanId`/`pendingScheduleDraftId`
 * when the validator tests the first `anyOf` variant and finds those fields
 * additional.
 *
 * Mutual exclusion between `pendingActionId`, `pendingPlanId`, and
 * `pendingScheduleDraftId` is enforced by the route's
 * `validateRawContinuationBody` preValidation hook — not here. All fields
 * remain explicitly declared (`additionalProperties: false` still rejects any
 * unknown property), satisfying H7.
 */
export const ToolResultMessagePartSchema = Type.Object({
    type: Type.String({ pattern: '^tool-.+' }),
    /** `output-error` (PR #319 follow-up item 8): the call threw; `errorText` replaces `output`. */
    state: Type.Union([Type.Literal('output-available'), Type.Literal('output-error')]),
    toolName: Type.Optional(Type.String({ minLength: 1 })),
    toolCallId: Type.Optional(Type.String({ minLength: 1 })),
    /** PR #319 review R4 — the call's arguments, replayed to the next model turn. Absent on older rows. */
    input: Type.Optional(Type.Unknown()),
    /** Present when `state` is `output-available`. */
    output: Type.Optional(Type.Unknown()),
    /** Present when `state` is `output-error`. */
    errorText: Type.Optional(Type.String()),
    outputStatus: Type.Optional(Type.Number()),
    outputCode: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    outputMessage: Type.Optional(Type.Union([Type.String(), Type.Null()])),
});
export const ChatBodySchema = Type.Object({
    ...ChatBodyBaseProperties,
    /**
     * Plan 05-09 (H7) — when present, the runtime executes the bound
     * `tf_approval.payload` directly. Explicitly declared so unknown fields
     * are still rejected by `additionalProperties: false`.
     */
    pendingActionId: Type.Optional(UuidSchema),
    /**
     * COP-057 / S05 executing-plan continuation. When present, the runtime
     * resumes only the server-persisted, hash-bound `tf_plan` envelope.
     */
    pendingPlanId: Type.Optional(UuidSchema),
    /**
     * Plan 02-01 (D-22) — schedule-draft continuation. Analogous to
     * `pendingPlanId`: when present, the runtime loads the caller/session
     * draft (RLS-enforced; cross-user → 404 per D-23), asserts
     * `status = 'approved'` (defense — approve happens via the decide
     * endpoint, not the chat body), and injects a synthetic
     * `create_schedule` tool result `{ scheduleId, nextRunAt, enabled }`
     * (no other metadata; no prompt content). Does NOT re-execute
     * `create_schedule` and does NOT re-evaluate plan-mode activation
     * (inherits the originating turn's mode per Phase 1 D-15/D-16).
     *
     * Mutual exclusion against `pendingActionId`/`pendingPlanId` is enforced
     * by `validateRawContinuationBody` in the chat route (D-22).
     */
    pendingScheduleDraftId: Type.Optional(UuidSchema),
}, { additionalProperties: false });
/**
 * Wire shape of the HTTP 422 body returned by `POST /chat` when the
 * resolved track has zero `expose: true` annotated operations AND the
 * principal lacks the admin escape hatch (D-14 / D-15).
 *
 * - `error: 'no_copilot_tools_configured'` — stable snake_case discriminant
 *   shared with `NoCopilotToolsConfiguredError.code` so clients can branch
 *   on a literal string without parsing free-form messages.
 * - `message: string` — human-readable explanation (UI-renderable).
 * - `trackName: string` — already public surface (URL path / widget setup
 *   card) — used to render the right track name in the operator hint.
 * - `warnings: string[]` — operator-facing compiler skip-reasons emitted by
 *   `compileNamedToolDescriptors` (long-name overflow, duplicate-name,
 *   schema-collision, missing kind/authz). Surfaces in the admin SPA + dev
 *   console so the operator can fix the spec annotations. May be empty when
 *   the spec simply has no annotated operations at all (no compiler input
 *   to reject) — Test 1 in 04-10 pins that case.
 */
/**
 * COP-158 D7 — a `POST /chat` refusal. `409 duplicate_message` names the
 * session that already holds the client's message (the resend may name no
 * session); the other refusals carry only the envelope.
 */
export const ChatRefusalResponseSchema = Type.Object({
    error: Type.String(),
    message: Type.String(),
    sessionId: Type.Optional(UuidSchema),
});
export const NoCopilotToolsConfiguredErrorBodySchema = Type.Object({
    error: Type.Literal('no_copilot_tools_configured'),
    message: Type.String(),
    trackName: Type.String(),
    warnings: Type.Array(Type.String()),
});
/**
 * ATRU-02 / COP-134 D2 — the `data-*` stream part types that represent an
 * action the assistant turn TOOK.
 *
 * A part type belongs in this list ONLY if it represents an action the turn
 * took, NEVER the outcome of an earlier one. Terminal / status parts
 * (`data-approval-resolved`, `data-memory-saved`, `data-memory-dismissed`,
 * `data-tf-schedule-preview-resolved`, `data-forget-memory-removed`, …) are
 * the RESULT of an action a previous turn took; counting them would inflate a
 * turn that only carried a resolution.
 *
 * ONE consumer remains: the hub's `detectNarrationWithoutAction`
 * (`hub/agent/runtime/narration-detector.ts`) suppresses the narration counter
 * from it, and `narration-detector.test.ts` pins that it re-exports THIS
 * binding rather than a copy.
 *
 * COP-135 D6 (TACT-06) — the browser was the second consumer: its
 * `summarizeTurnActivity` counted a turn's actions from this list. That count
 * is DELETED. The client no longer decides what an action is; the hub records
 * every action in `tf_turn_action` and sends the rendered rows in
 * `data-turn-actions`. TACT-06 named this constant among the deletions on the
 * assumption that the client was its only consumer; it is not, and the
 * narration counter is out of scope, so the constant stays. Delete it only when
 * the narration counter no longer needs it.
 */
export const ACTION_CARD_PART_TYPES = [
    'data-approval-pending',
    'data-tf-plan-pending',
    'data-tf-schedule-preview-pending',
    'data-memory-suggestion',
    'data-doc-share-suggestion',
    'data-forget-memory',
    'data-db-table',
    'data-skill-loaded',
];
/**
 * COP-135 D1 — the closed outcome vocabulary of ONE action inside a turn.
 *
 * Mirrors the `turn_action_outcome` pgEnum (`hub/src/schema/agent.ts`) and the
 * hub-side `TurnActionOutcome` union (`hub/src/chat/turn-actions/record.ts`)
 * exactly. A gated call moves `awaiting_approval` → `succeeded` / `failed` /
 * `denied`; the approval is a STATE of the action, never a second action
 * (COP-135 D5).
 *
 * `not_run` is also the explicit fallback for a record whose outcome is not
 * recognised (a row written before this vocabulary existed): the action still
 * renders, so an old session UNDER-reports visibly instead of inventing
 * (COP-135 Consequences).
 *
 * `unknown` (COP-138) is the one member that describes the LEDGER rather than
 * the action: the side effect was dispatched and its answer never reached the
 * database. It is deliberately NOT collapsed into `failed` on any surface —
 * `failed` reads as "this did not happen" and invites the user to run it again,
 * and re-running a write that DID happen is the harm the member exists to
 * prevent.
 *
 * `edited` (COP-143) is the user sending a proposal BACK for a change. It ends
 * the proposed call exactly as `denied` does, which is why one member covered
 * both — but `denied` is a word about the USER and names a decision they did
 * not make. Same bar as `unknown`: the existing members misstate it.
 *
 * `awaiting_job` (COP-156 D6) is a track call that the track accepted as a
 * long-running job: the call is parked and its result comes later. `running`
 * misstates it — nothing runs in the hub — and it is not a final outcome.
 */
export const TurnActionOutcomeSchema = Type.Union([
    Type.Literal('running'),
    Type.Literal('awaiting_approval'),
    Type.Literal('denied'),
    Type.Literal('succeeded'),
    Type.Literal('failed'),
    Type.Literal('not_run'),
    Type.Literal('unknown'),
    Type.Literal('edited'),
    Type.Literal('awaiting_job'),
]);
/**
 * COP-135 D1 — one action of a turn, as the user sees it: a name and what
 * became of it. Never a count (COP-135 D6 withdraws the `N action(s)` format).
 *
 * `actionId` is stable WITHIN the turn — the `toolCallId`, or the plan step key
 * `plan:<planId>:<stepIndex>` — so a re-emitted list replaces rows rather than
 * appending them.
 *
 * `detail` is SHORT and non-sensitive: a typed error code, never a payload, a
 * tool result, or a provider error body (HUB-024, TACT-08). It is OMITTED when
 * there is nothing to show — an absent key means "nothing to show", never
 * "failed", the same rule COP-134 D1 set for the approval card's `execution`
 * field (COP-131). Producers must omit the key, not emit `null`: TypeBox does
 * not strip an emitted `null`.
 *
 * The TS type is named `ActionRow` because that is the name COP-135 D1 gives
 * this shape, and because the hub's `TurnActionRow` already names the DB row
 * that `buildTurnActions` consumes — the two must not be confused.
 */
export const TurnActionRowSchema = Type.Object({
    actionId: Type.String({ minLength: 1 }),
    name: Type.String({ minLength: 1 }),
    outcome: TurnActionOutcomeSchema,
    // Bounded because "short typed code" is the contract, not a hope: the ledger
    // writer (`recordTurnAction`) is the only producer and writes codes only.
    detail: Type.Optional(Type.String({ maxLength: 200 })),
});
/**
 * COP-135 — payload of the `data-turn-actions` SSE part: the WHOLE rendered
 * action list for one turn, re-emitted after every state change (COP-135 D3).
 *
 * The browser renders exactly this and infers nothing (COP-135 D1). Both the
 * live seam and the reload seam obtain `actions` from the single producer,
 * `buildTurnActions` (COP-135 D2).
 *
 * Like its sibling part schemas (`ApprovalPendingPartSchema`,
 * `ApprovalResolvedPartSchema`) this object does NOT set
 * `additionalProperties: false` — an unknown key is tolerated so an older
 * consumer keeps working when the shape grows.
 * `hub/agent/runtime/stream-parts.test.ts` pins that configured behaviour, so a
 * later change to it is visible rather than silent.
 */
export const TurnActionsPartDataSchema = Type.Object({
    turnId: UuidSchema,
    actions: Type.Array(TurnActionRowSchema),
});
//# sourceMappingURL=chat.js.map